import React, { useState } from 'react';

import { Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Typography, Chip, Divider, Alert } from '@mui/material';

import { useRefreshTokenDebug } from '../../hooks/useRefreshTokenDebug';

interface RefreshTokenDebugProps {
  show?: boolean;
}

export const RefreshTokenDebug: React.FC<RefreshTokenDebugProps> = ({ show = false }) => {
  const { debugInfo, updateDebugInfo, forceRefresh, clearCookies } = useRefreshTokenDebug();
  const [healthStatus] = useState<'idle' | 'checking' | 'healthy' | 'unhealthy'>('idle');

  if (!show) {
    return null;
  }

  return (
    <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, maxWidth: 400 }}>
      <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', color: 'white' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
            🔐 Refresh Token Debug
          </Typography>

          <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />

          {/* Cookies Status */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'lightblue' }}>
              Cookies:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Access: ${debugInfo.cookies.hasAccessToken ? '✅' : '❌'}`}
                size="small"
                color={debugInfo.cookies.hasAccessToken ? 'success' : 'error'}
                variant="outlined"
              />
              <Chip
                label={`Refresh: ${debugInfo.cookies.hasRefreshToken ? '✅' : '❌'}`}
                size="small"
                color={debugInfo.cookies.hasRefreshToken ? 'success' : 'error'}
                variant="outlined"
              />
              <Chip
                label={`Expiry: ${debugInfo.cookies.hasExpiry ? '✅' : '❌'}`}
                size="small"
                color={debugInfo.cookies.hasExpiry ? 'success' : 'error'}
                variant="outlined"
              />
            </Box>
            {debugInfo.cookies.expiryDate && (
              <Typography variant="caption" sx={{ color: 'lightgray', display: 'block', mt: 1 }}>
                Expira: {debugInfo.cookies.expiryDate.toLocaleString()}
              </Typography>
            )}

            {/* Nota sobre cookies HttpOnly */}
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'lightgray', display: 'block' }}>
                refresh_token é HttpOnly - gerenciado automaticamente pelo navegador
              </Typography>
            </Box>
          </Box>

          {/* Token Validation */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'lightblue' }}>
              Token:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Valid: ${debugInfo.tokenValidation.isValid ? '✅' : '❌'}`}
                size="small"
                color={debugInfo.tokenValidation.isValid ? 'success' : 'error'}
                variant="outlined"
              />
              <Chip
                label={`Expired: ${debugInfo.tokenValidation.isExpired ? '❌' : '✅'}`}
                size="small"
                color={debugInfo.tokenValidation.isExpired ? 'error' : 'success'}
                variant="outlined"
              />
              <Chip
                label={`Needs Refresh: ${debugInfo.tokenValidation.needsRefresh ? '⚠️' : '✅'}`}
                size="small"
                color={debugInfo.tokenValidation.needsRefresh ? 'warning' : 'success'}
                variant="outlined"
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'lightgray', display: 'block', mt: 1 }}>
              Tempo restante: {debugInfo.tokenValidation.timeRemaining}
            </Typography>
          </Box>

          {/* Service Status */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'lightblue' }}>
              Serviço:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Refreshing: ${debugInfo.serviceStatus.isRefreshing ? '🔄' : '⏸️'}`}
                size="small"
                color={debugInfo.serviceStatus.isRefreshing ? 'warning' : 'default'}
                variant="outlined"
              />
              <Chip
                label={`Retries: ${debugInfo.serviceStatus.retryCount}`}
                size="small"
                color="info"
                variant="outlined"
              />
              <Chip
                label={`Queue: ${debugInfo.serviceStatus.queueLength}`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Box>
          </Box>

          {/* Health Check Status */}
          {healthStatus !== 'idle' && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity={
                  healthStatus === 'healthy'
                    ? 'success'
                    : healthStatus === 'unhealthy'
                      ? 'error'
                      : 'info'
                }
                sx={{ backgroundColor: 'transparent' }}
              >
                Health Check:{' '}
                {healthStatus === 'checking'
                  ? 'Verificando...'
                  : healthStatus === 'healthy'
                    ? 'Saudável'
                    : 'Não saudável'}
              </Alert>
            </Box>
          )}

          <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={forceRefresh}
              disabled={debugInfo.serviceStatus.isRefreshing}
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Force Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={clearCookies}
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Clear Cookies
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={updateDebugInfo}
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Update
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
